import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  TextField,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Rating,
  Snackbar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Search,
  MusicNote,
  ContentCopy,
  ExpandMore,
} from '@mui/icons-material';
import {
  useAIReferences,
  useCreateAIReference,
  useUpdateAIReference,
  useDeleteAIReference,
} from '../../hooks/useAIGeneration';
import type { AIReferenceSong } from '../../api/types';
import { useOrgFilter } from '../../contexts/OrganizationContext';

const SONGFORGE_ACCENT_COLOR = '#8b5cf6';

export const ReferencesSection: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingRef, setEditingRef] = useState<AIReferenceSong | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const orgFilter = useOrgFilter();

  const { data: references, isLoading, error } = useAIReferences({
    organization_id: orgFilter,
    search: searchQuery || undefined,
  });

  const createMutation = useCreateAIReference();
  const updateMutation = useUpdateAIReference();
  const deleteMutation = useDeleteAIReference();

  const [formData, setFormData] = useState({
    title: '',
    artist: '',
    reason: '',
    musical_key: '',
    bpm: '',
    time_signature: '',
    chord_progression: '',
    genre: '',
    mood: '',
    energy_level: 5,
    analysis_prompt: '',
    analysis_response: '',
  });

  const handleOpenCreate = () => {
    setFormData({
      title: '',
      artist: '',
      reason: '',
      musical_key: '',
      bpm: '',
      time_signature: '',
      chord_progression: '',
      genre: '',
      mood: '',
      energy_level: 5,
      analysis_prompt: '',
      analysis_response: '',
    });
    setEditingRef(null);
    setCreateDialogOpen(true);
  };

  const handleOpenEdit = (ref: AIReferenceSong) => {
    setFormData({
      title: ref.title,
      artist: ref.artist,
      reason: ref.reason || '',
      musical_key: ref.musical_key || '',
      bpm: ref.bpm?.toString() || '',
      time_signature: ref.time_signature || '',
      chord_progression: ref.chord_progression || '',
      genre: ref.genre || '',
      mood: ref.mood || '',
      energy_level: ref.energy_level || 5,
      analysis_prompt: ref.analysis_prompt || '',
      analysis_response: ref.analysis_response || '',
    });
    setEditingRef(ref);
    setCreateDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const data = {
        organization_id: orgFilter || '',
        title: formData.title,
        artist: formData.artist,
        reason: formData.reason || undefined,
        musical_key: formData.musical_key || undefined,
        bpm: formData.bpm ? parseInt(formData.bpm) : undefined,
        time_signature: formData.time_signature || undefined,
        chord_progression: formData.chord_progression || undefined,
        genre: formData.genre || undefined,
        mood: formData.mood || undefined,
        energy_level: formData.energy_level,
        analysis_prompt: formData.analysis_prompt || undefined,
        analysis_response: formData.analysis_response || undefined,
      };

      if (editingRef) {
        await updateMutation.mutateAsync({ id: editingRef.id, data });
      } else {
        await createMutation.mutateAsync(data);
      }
      setCreateDialogOpen(false);
    } catch (err) {
      console.error('Failed to save reference:', err);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this reference song?')) {
      await deleteMutation.mutateAsync(id);
    }
  };

  const handleCopyAnalysisPrompt = (ref: AIReferenceSong) => {
    const prompt = ref.analysis_prompt || generateDefaultAnalysisPrompt(ref);
    navigator.clipboard.writeText(prompt);
    setSnackbarMessage('Analysis prompt copied to clipboard');
    setSnackbarOpen(true);
  };

  const generateDefaultAnalysisPrompt = (ref: AIReferenceSong) => {
    return `Analyze the following song for AI music generation purposes:

Song: "${ref.title}" by ${ref.artist}
${ref.reason ? `Reason for reference: ${ref.reason}` : ''}

Please provide:
1. Musical key and mode
2. BPM estimate
3. Time signature
4. Main chord progression
5. Genre classification
6. Mood/atmosphere description
7. Energy level (1-10)
8. Notable production elements
9. Suggested style prompt for AI music generation`;
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load references: {(error as Error)?.message}
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <TextField
          size="small"
          placeholder="Search references..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} />,
          }}
          sx={{ width: 300 }}
        />

        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleOpenCreate}
          sx={{ bgcolor: SONGFORGE_ACCENT_COLOR }}
        >
          Add Reference
        </Button>
      </Box>

      <Grid container spacing={2}>
        {references?.map((ref) => (
          <Grid item xs={12} md={6} lg={4} key={ref.id}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Box>
                    <Typography variant="h6" noWrap>
                      {ref.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {ref.artist}
                    </Typography>
                  </Box>
                  <Box>
                    <Tooltip title="Copy Analysis Prompt">
                      <IconButton size="small" onClick={() => handleCopyAnalysisPrompt(ref)}>
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <IconButton size="small" onClick={() => handleOpenEdit(ref)}>
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(ref.id)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>

                {ref.reason && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {ref.reason}
                  </Typography>
                )}

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                  {ref.genre && <Chip label={ref.genre} size="small" />}
                  {ref.mood && <Chip label={ref.mood} size="small" variant="outlined" />}
                  {ref.bpm && <Chip label={`${ref.bpm} BPM`} size="small" variant="outlined" />}
                  {ref.musical_key && <Chip label={ref.musical_key} size="small" variant="outlined" />}
                </Box>

                {ref.energy_level && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">Energy:</Typography>
                    <Rating value={ref.energy_level} max={10} size="small" readOnly />
                  </Box>
                )}

                {ref.analysis_response && (
                  <Accordion sx={{ mt: 2, '&:before': { display: 'none' } }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography variant="body2">View Analysis</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: 'pre-wrap', fontSize: '0.75rem' }}
                      >
                        {ref.analysis_response}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
        {(!references || references.length === 0) && (
          <Grid item xs={12}>
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <MusicNote sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography color="text.secondary">
                No reference songs yet. Add songs that inspire the kind of music you want to generate.
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Create/Edit Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingRef ? 'Edit Reference Song' : 'Add Reference Song'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Song Title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                fullWidth
              />
              <TextField
                label="Artist"
                value={formData.artist}
                onChange={(e) => setFormData({ ...formData, artist: e.target.value })}
                required
                fullWidth
              />
            </Box>

            <TextField
              label="Why this reference?"
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              placeholder="e.g., Love the energy and chord progression"
              fullWidth
            />

            <Typography variant="subtitle2" sx={{ mt: 1 }}>Musical Properties</Typography>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Key"
                value={formData.musical_key}
                onChange={(e) => setFormData({ ...formData, musical_key: e.target.value })}
                placeholder="e.g., C major, F# minor"
                fullWidth
              />
              <TextField
                label="BPM"
                type="number"
                value={formData.bpm}
                onChange={(e) => setFormData({ ...formData, bpm: e.target.value })}
                placeholder="e.g., 120"
                fullWidth
              />
              <TextField
                label="Time Signature"
                value={formData.time_signature}
                onChange={(e) => setFormData({ ...formData, time_signature: e.target.value })}
                placeholder="e.g., 4/4"
                fullWidth
              />
            </Box>

            <TextField
              label="Chord Progression"
              value={formData.chord_progression}
              onChange={(e) => setFormData({ ...formData, chord_progression: e.target.value })}
              placeholder="e.g., I-V-vi-IV, Am-F-C-G"
              fullWidth
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Genre"
                value={formData.genre}
                onChange={(e) => setFormData({ ...formData, genre: e.target.value })}
                placeholder="e.g., Pop, Electronic"
                fullWidth
              />
              <TextField
                label="Mood"
                value={formData.mood}
                onChange={(e) => setFormData({ ...formData, mood: e.target.value })}
                placeholder="e.g., Upbeat, Melancholic"
                fullWidth
              />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography>Energy Level:</Typography>
              <Rating
                value={formData.energy_level}
                max={10}
                onChange={(_, value) => setFormData({ ...formData, energy_level: value || 5 })}
              />
              <Typography color="text.secondary">({formData.energy_level}/10)</Typography>
            </Box>

            <Typography variant="subtitle2" sx={{ mt: 1 }}>Analysis</Typography>

            <TextField
              label="Analysis Prompt (for Claude)"
              value={formData.analysis_prompt}
              onChange={(e) => setFormData({ ...formData, analysis_prompt: e.target.value })}
              multiline
              rows={4}
              placeholder="The prompt to send to Claude for analysis..."
              fullWidth
            />

            <TextField
              label="Analysis Response (from Claude)"
              value={formData.analysis_response}
              onChange={(e) => setFormData({ ...formData, analysis_response: e.target.value })}
              multiline
              rows={6}
              placeholder="Paste Claude's analysis response here..."
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.title || !formData.artist || createMutation.isPending || updateMutation.isPending}
          >
            {editingRef ? 'Save' : 'Add Reference'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default ReferencesSection;
